import React from "react";
import clsx from "clsx";
import axios from "axios";
import { closeSnackbar } from "notistack";
import {
  Dialog,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
} from "@tiny/components";
import { useTinyHost } from "@tiny/host-contract";
import {
  usePostSegmentUploadMutation,
  usePutPreSignedUrlGenerateMutation,
} from "@tiny/api";
import FileBlankOutline from "@tiny/icons/generated/FileBlankOutline";
import UploadOutline from "@tiny/icons/generated/UploadOutline";
import Info2Outline from "@tiny/icons/generated/Info2Outline";
import TrashEmpty from "@tiny/icons/generated/TrashEmpty";
import WarningOutline from "@tiny/icons/generated/WarningOutline";
import { useStyles } from "./LargeFileUploadDialog.styles";
import { getSampleCsvData } from "../TinyImporterSample/TinyImporter.helpers";

const MAX_FILE_SIZE_MB = 100;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export interface LargeFileUploadDialogProps {
  open: boolean;
  onClose: () => void;
  audienceName: string;
  initialFile?: File | null;
  existingSegmentId?: string;
  onSuccess?: () => void;
}

export function LargeFileUploadDialog({
  open,
  onClose,
  audienceName,
  initialFile,
  existingSegmentId,
  onSuccess,
}: LargeFileUploadDialogProps) {
  const classes = useStyles();

  const [selectedFile, setSelectedFile] = React.useState<File | null>(
    initialFile ?? null
  );
  const [isDragging, setIsDragging] = React.useState(false);
  const [fileError, setFileError] = React.useState("");
  const [isUploadingToS3, setIsUploadingToS3] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [segmentId, setSegmentId] = React.useState<string | undefined>(
    undefined
  );

  React.useEffect(() => {
    if (open) {
      setSelectedFile(initialFile ?? null);
      setUploadProgress(0);
      setSegmentId(undefined);
      setFileError("");
    }
  }, [open, initialFile]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { notify } = useTinyHost();

  const { mutateAsync: postSegmentUploadMutateAsync, isLoading: isCreating } =
    usePostSegmentUploadMutation();
  const {
    mutateAsync: putPreSignedUrlGenerateMutateAsync,
    isLoading: isGeneratingUrl,
  } = usePutPreSignedUrlGenerateMutation();

  const isUploading = isCreating || isGeneratingUrl || isUploadingToS3;

  const validateAndSetFile = (file: File) => {
    setFileError("");
    if (!file.name.endsWith(".csv") && file.type !== "text/csv") {
      setFileError("Only CSV files are accepted.");
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFileError(
        `File exceeds the ${MAX_FILE_SIZE_MB} MB limit. Please split it into smaller files.`
      );
      return;
    }
    setSelectedFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) validateAndSetFile(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) validateAndSetFile(file);
    e.target.value = "";
  };

  const handleDownloadSample = () => {
    const csv = getSampleCsvData();
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_contacts.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    try {
      const resolvedSegmentId =
        existingSegmentId ??
        segmentId ??
        (
          await postSegmentUploadMutateAsync({
            data: { name: audienceName },
          })
        ).id!;
      if (!existingSegmentId && !segmentId) setSegmentId(resolvedSegmentId);
      const { preSignedUrl } = await putPreSignedUrlGenerateMutateAsync({
        data: { segmentId: resolvedSegmentId },
      });
      setIsUploadingToS3(true);
      await axios.put(preSignedUrl, selectedFile, {
        headers: { "Content-Type": selectedFile.type || "text/csv" },
        onUploadProgress: (e) => {
          if (e.total)
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      setIsUploadingToS3(false);
      onClose();
      onSuccess?.();
      closeSnackbar();
      notify.showUpload?.({
        variant: "success",
        message: "Uploaded successfully!",
        description: `Your [${audienceName}] audience has been uploaded. We're now processing the data in the background — we'll email you once it's ready.`,
        primaryCtaActionText: "Close",
        primaryCtaActionFunction: () => closeSnackbar(),
        persist: true,
      });
    } catch {
      setIsUploadingToS3(false);
      closeSnackbar();
      notify.showUpload?.({
        variant: "failed",
        message: "Uploading error!",
        description:
          "It seems the file upload failed. Please contact the support team.",
        showClose: true,
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      disableBackdropClose
    >
      <DialogTitle
        onClose={onClose}
        subtitle="Large files may take a few minutes to upload — please keep this window open."
      >
        Upload CSV File
      </DialogTitle>

      <DialogContent>
        <div className={classes.dialogInfoBox}>
          <div className={classes.csvRow}>
            <WarningOutline className={classes.csvWarningIcon} />
            <div className={classes.csvRowBody}>
              <span className={classes.csvRowText}>
                <strong>Email</strong> column is required. Use the sample file
                as a reference for the correct column names and formats —
                unrecognized columns will be ignored, and incorrect formats
                (e.g. dates, status values) may cause import errors or missing
                data.
              </span>
              <button
                type="button"
                className={classes.sampleDownloadBtn}
                onClick={handleDownloadSample}
              >
                Download sample CSV
              </button>
            </div>
          </div>
          <div className={classes.rowDivider} />
          <div className={classes.infoRow}>
            <Info2Outline className={classes.infoIcon} />
            <p className={classes.infoText}>
              Once uploaded, we&apos;ll validate and process your data in the
              background and send you an email when it&apos;s ready.
            </p>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: "none" }}
          onChange={handleFileInputChange}
        />

        {!selectedFile ? (
          <div
            className={clsx(
              classes.dropZone,
              isDragging && classes.dropZoneDragging,
              fileError && classes.dropZoneError
            )}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsDragging(false);
            }}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div
              className={clsx(
                classes.dropZoneIconCircle,
                isDragging && classes.dropZoneIconCircleDragging
              )}
            >
              <UploadOutline
                className={clsx(
                  classes.dropZoneCloudIcon,
                  isDragging && classes.dropZoneCloudIconDragging
                )}
              />
            </div>

            <div className={classes.dropZoneTitle}>
              Drag &amp; drop your file here
            </div>
            <div className={classes.dropZoneHint}>or</div>

            <Button
              size="small"
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              Browse files
            </Button>

            <div className={classes.dropZoneMeta}>
              <span className={classes.dropZoneMetaText}>
                Supported format:{" "}
                <strong className={classes.dropZoneMetaStrong}>CSV</strong>
              </span>
              <span className={classes.dropZoneMetaText}>
                Max size:{" "}
                <strong className={classes.dropZoneMetaStrong}>
                  {MAX_FILE_SIZE_MB} MB
                </strong>
              </span>
            </div>
          </div>
        ) : (
          <div className={classes.fileCard}>
            <div className={classes.fileCardRow}>
              <div className={classes.fileIconBox}>
                <FileBlankOutline className={classes.fileIcon} />
              </div>

              <div className={classes.fileInfo}>
                <div className={classes.fileName}>{selectedFile.name}</div>
              </div>

              {!isUploading && (
                <IconButton
                  size="small"
                  onClick={handleRemoveFile}
                  title="Remove file"
                >
                  <TrashEmpty style={{ width: 18, height: 18 }} />
                </IconButton>
              )}
            </div>

            {isUploadingToS3 && (
              <div className={classes.progressWrap}>
                <div className={classes.progressRow}>
                  <span>{uploadProgress}%</span>
                  <span className={classes.progressPercent}>Uploading…</span>
                </div>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </div>
            )}

            {!isUploading && (
              <button
                type="button"
                className={classes.changeFileLink}
                onClick={() => fileInputRef.current?.click()}
              >
                Choose a different file
              </button>
            )}
          </div>
        )}

        {fileError && (
          <div className={classes.errorBox}>
            <WarningOutline className={classes.errorIcon} />
            <span className={classes.errorText}>{fileError}</span>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={isUploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selectedFile || isUploading}
          onClick={handleUpload}
          loading={isUploading}
        >
          {isUploading ? "Uploading…" : "Upload file"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
